import React, { useEffect, useState } from 'react'
import Input from '../../components/input';
import useAxiosInstance from '../../hooks/useAxios';
import { toast } from 'react-toastify';
import { TableCustom } from '../../components/Table';
import { Paginator } from '../../components/Paginator';
import { ModalComp } from '../../components/Modal';
import FormRender from '../../components/FormRender';
import Select from "react-select";
import { weekdaysOptions, dayOptions, monthsOptions } from '../../helper';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Brush } from 'recharts';
import { Warning } from '@mui/icons-material';
import { MdWarningAmber } from 'react-icons/md';

const TABLE_FORM_SETTINGS = [{ type: "table", label: "", saveKey: "tableData" }];

const CHART_COLORS = ['#000000', '#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0891b2', '#db2777'];

const MetricQueryExplorer = () => {
    const axiosInstance = useAxiosInstance();

    const [sqlQuery, setSqlQuery] = useState('');
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');
    const [selectedDatabase, setSelectedDatabase] = useState('');

    const [validationResult, setValidationResult] = useState(null);
    const [isValidating, setIsValidating] = useState(false);

    const [isSaving, setIsSaving] = useState(false);

    const [queries, setQueries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [intermidiatePageNumber, setIntermidiatePageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [databases, setDatabases] = useState([]);

    const [expandedQueryId, setExpandedQueryId] = useState(null);

    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedQueryId, setSelectedQueryId] = useState(null);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [scheduleForm, setScheduleForm] = useState({
        time: "",
        start_date: "",
        mode: "",
        scheduler_mode: "other",
        time_setting_meta: { week_days: [], months: [], days: [] },
        api: "", headers: "", payload: "", request_type: "POST", name: "", queue: ""
    });

    const [querySchedules, setQuerySchedules] = useState([]);

    const [actionLoading, setActionLoading] = useState({});

    const [availableColumns, setAvailableColumns] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [overallStats, setOverallStats] = useState(null);
    const [anomalies, setAnomalies] = useState([]);
    const [chartError, setChartError] = useState(null);
    const [loadingChart, setLoadingChart] = useState(false);
    const [days, setDays] = useState('7');
    const [highlightedMetrics, setHighlightedMetrics] = useState([]);

    const [tableData, setTableData] = useState(null);
    const [tableColumns, setTableColumns] = useState([]);
    const [collectedOn, setCollectedOn] = useState(null);
    const [loadingTable, setLoadingTable] = useState(false);
    const [tableDays, setTableDays] = useState('7');
    const [activeTab, setActiveTab] = useState('chart');

    const [executionLogs, setExecutionLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [selectedLogId, setSelectedLogId] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState({});

    const [showEditModal, setShowEditModal] = useState(false);
    const [editQueryId, setEditQueryId] = useState(null);
    const [editForm, setEditForm] = useState({ label: '', sql: '', description: '', database: '' });
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editModeQueryId, setEditModeQueryId] = useState(null);

    const TABLE_COLUMNS = [
        { key: 'id', label: 'ID', width: '5%' },
        { key: 'label', label: 'Label', width: '15%' },
        { key: 'database', label: 'Database', width: '10%' },
        { key: 'description', label: 'Description', width: '20%' },
        { key: 'createdOn', label: 'Created', width: '10%' },
    ];

    useEffect(() => {
        axiosInstance.get('wkl/monitoring/metric-queries/databases/')
            .then(res => {
                const dbData = Array.isArray(res?.data?.data) ? res.data.data : 
                               Array.isArray(res?.data) ? res.data : [];
                setDatabases(dbData);
                if (dbData.length > 0) {
                    setSelectedDatabase(dbData[0]);
                }
            })
            .catch(err => console.error('Error fetching databases:', err));
    }, []);

    useEffect(() => {
        setLoading(true);
        const controller = new AbortController();
        axiosInstance.get('wkl/monitoring/metric-queries/', {
            params: { page, page_size: pageSize },
            signal: controller.signal
        })
            .then(res => {
                setQueries(res.data);
                setLoading(false);
                setIntermidiatePageNumber(res.data.current_page);
            })
            .catch(err => {
                console.error('Error fetching queries:', err);
                setLoading(false);
            });
        return () => controller.abort();
    }, [page, pageSize]);

    const formateDate = (dateGiven) => {
        if (!dateGiven) return '—';
        try {
            const d = new Date(dateGiven);
            if (isNaN(d.getTime())) return '—';
            return d.toISOString().slice(0, 19).replace('T', ' ');
        } catch {
            return '—';
        }
    };

    const handleValidate = () => {
        if (!sqlQuery.trim()) {
            toast.error('Please enter a SQL query');
            return;
        }
        if (!selectedDatabase) {
            toast.error('Please select a database');
            return;
        }

        setIsValidating(true);
        setValidationResult(null);
        const controller = new AbortController();
        axiosInstance.post('wkl/monitoring/metric-queries/validate/', {
            sql: sqlQuery,
            database: selectedDatabase
        }, { signal: controller.signal })
            .then(res => {
                setValidationResult({ valid: true, data: res.data });
                toast.success('Query is valid!');
            })
            .catch(err => {
                const errorMsg = err.response?.data?.message || err.message || 'Validation failed';
                setValidationResult({ valid: false, error: errorMsg });
                toast.error(errorMsg);
            })
            .finally(() => setIsValidating(false));
        return () => controller.abort();
    };

    const handleSave = () => {
        if (!sqlQuery.trim()) {
            toast.error('Please enter a SQL query');
            return;
        }
        if (!label.trim()) {
            toast.error('Please enter a label for the query');
            return;
        }
        if (!selectedDatabase) {
            toast.error('Please select a database');
            return;
        }

        setIsSaving(true);
        const controller = new AbortController();
        axiosInstance.post('wkl/monitoring/metric-queries/validate/', {
            sql: sqlQuery,
            database: selectedDatabase
        }, { signal: controller.signal })
            .then(() => {
                return axiosInstance.post('wkl/monitoring/metric-queries/create/', {
                    label, sql: sqlQuery, database: selectedDatabase, description
                }, { signal: controller.signal });
            })
            .then(() => {
                toast.success('Query saved successfully!');
                setSqlQuery('');
                setLabel('');
                setDescription('');
                setValidationResult(null);
                setPage(1);
                axiosInstance.get('wkl/monitoring/metric-queries/', { params: { page: 1, page_size: pageSize } })
                    .then(res => setQueries(res.data));
            })
            .catch(err => {
                toast.error(err.response?.data?.message || err.message || 'Failed to save query');
            })
            .finally(() => setIsSaving(false));
        return () => controller.abort();
    };

    const handleTrigger = (queryId) => {
        setActionLoading(prev => ({ ...prev, [queryId]: 'trigger' }));
        axiosInstance.post(`wkl/monitoring/metric-queries/${queryId}/trigger/`)
            .then(() => toast.success('Query triggered successfully!'))
            .catch(err => toast.error(err.response?.data?.message || 'Failed to trigger query'))
            .finally(() => setActionLoading(prev => ({ ...prev, [queryId]: null })));
    };

    const handleDelete = (queryId) => {
        toast.info('Query deletion not available. Use schedule management instead.');
    };

    const handleExpand = (queryId) => {
        if (expandedQueryId === queryId) {
            setExpandedQueryId(null);
        } else {
            setExpandedQueryId(queryId);
            setAvailableColumns([]);
            setChartData(null);
            setOverallStats(null);
            setAnomalies([]);
            setChartError(null);
            setHighlightedMetrics([]);
            setTableData(null);
            setTableColumns([]);
            setCollectedOn(null);
            setActiveTab('chart');
            fetchChartData(queryId);
            setExecutionLogs([]);
            setSelectedLogId(null);
            fetchDataPoints(queryId);
            fetchSchedules(queryId);
            fetchExecutionLogs(queryId);
        }
    };

    const fetchDataPoints = (queryId) => {
        axiosInstance.get(`wkl/monitoring/metric-queries/${queryId}/data-points/`)
            .then(res => {
                const executions = res?.data?.data || [];
                const latestExec = executions.length > 0 ? executions[0] : null;
                const successfulExec = executions.find(exec => exec.status === 'success' && exec.columns);
                const execToUse = successfulExec || latestExec;
                if (execToUse?.columns) {
                    setAvailableColumns(execToUse.columns);
                }
                if (execToUse?.id) {
                    fetchTableRows(execToUse.id);
                }
            })
            .catch(err => console.error('Error fetching data points:', err));
    };

    const fetchSchedules = (queryId) => {
        axiosInstance.get(`wkl/monitoring/metric-queries/${queryId}/schedules/`)
            .then(res => setQuerySchedules(res.data?.data || []))
            .catch(err => {
                console.error('Error fetching schedules:', err);
                setQuerySchedules([]);
            });
    };

    const fetchExecutionLogs = (queryId) => {
        setLoadingLogs(true);
        axiosInstance.get(`wkl/monitoring/metric-queries/${queryId}/data-points/`)
            .then(res => {
                setExecutionLogs(res?.data?.data || []);
            })
            .catch(err => {
                console.error('Error fetching execution logs:', err);
                setExecutionLogs([]);
            })
            .finally(() => setLoadingLogs(false));
    };

    const handleLoadLogData = (logId) => {
        setSelectedLogId(logId);
        fetchTableRows(logId);
    };

    const handleDownloadLog = (blobUrl) => {
        if (blobUrl) {
            window.open(blobUrl, '_blank');
        }
    };

    const formatDuration = (ms) => {
        if (ms === null || ms === undefined) return '—';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const fetchChartData = (queryId) => {
        setLoadingChart(true);
        setChartError(null);
        const controller = new AbortController();
        axiosInstance.get(`wkl/monitoring/metric-queries/${queryId}/chart/`, {
            params: { days },
            signal: controller.signal
        })
            .then(res => {
                const metricsData = res?.data?.data?.series || {};
                const metricNames = Object.keys(metricsData);
                let mergedData = [];

                metricNames.forEach(metricName => {
                    const metric = metricsData[metricName];
                    if (metric.points && Array.isArray(metric.points)) {
                        metric.points.forEach(point => {
                            const existing = mergedData.find(d => d.x === point.x);
                            if (existing) {
                                existing[metricName] = point.y;
                            } else {
                                mergedData.push({ x: point.x, [metricName]: point.y });
                            }
                        });
                    }
                });

                mergedData.sort((a, b) => new Date(a.x) - new Date(b.x));

                setChartData({
                    data: mergedData,
                    metrics: metricsData
                });
                setOverallStats(res?.data?.data?.overall_stats || null);
                setAnomalies(res?.data?.data?.anomalies || []);
                setHighlightedMetrics([]);
                setLoadingChart(false);
            })
            .catch(err => {
                if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
                const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to load chart data';
                toast.error(msg);
                setChartError(msg);
                setChartData(null);
                setOverallStats(null);
                setAnomalies([]);
                setLoadingChart(false);
            });
        return () => controller.abort();
    };

    const fetchTableRows = (executionId) => {
        setLoadingTable(true);
        axiosInstance.get(`wkl/monitoring/data-points/${executionId}/rows/`)
            .then(res => {
                const columns = res?.data?.data?.columns || [];
                const rows = res?.data?.data?.rows || [];
                const collected = res?.data?.data?.collected_on || null;
                const timestamps = columns.slice(1);
                const enrichedRows = collected ? rows.map(r => ({ ...r, collected_on: collected })) : rows;
                setTableData(enrichedRows);
                setTableColumns(columns);
                setCollectedOn(collected);
                setLoadingTable(false);
            })
            .catch(err => {
                console.error('Error fetching table rows:', err);
                setLoadingTable(false);
            });
    };

    const fetchTableDataByDays = (queryId) => {
        setLoadingTable(true);
        axiosInstance.get(`wkl/monitoring/metric-queries/${queryId}/data-points/rows/`, {
            params: { days: tableDays }
        })
            .then(res => {
                const columns = res?.data?.data?.columns || [];
                const rows = res?.data?.data?.rows || [];
                const timestamps = columns.slice(1);
                const enrichedRows = rows.map(r => ({
                    ...r,
                    collected_on: timestamps.length > 0 ? timestamps[timestamps.length - 1] : null
                }));
                setTableData(enrichedRows);
                setTableColumns(columns);
                setCollectedOn(timestamps.length > 0 ? timestamps[timestamps.length - 1] : null);
                setLoadingTable(false);
            })
            .catch(err => {
                toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load table data');
                setTableData(null);
                setTableColumns([]);
                setCollectedOn(null);
                setLoadingTable(false);
            });
    };

    const downloadTableAsCsv = () => {
        if (!tableData || tableData.length === 0) {
            toast.error('No data available to download');
            return;
        }

        if (!tableColumns || tableColumns.length === 0) {
            const allKeys = new Set();
            tableData.forEach(row => {
                Object.keys(row).forEach(k => {
                    if (k !== 'collected_on') allKeys.add(k);
                });
            });
            if (allKeys.size === 0) {
                toast.error('No data available to download');
                return;
            }
            const headers = ['metric', ...Array.from(allKeys).sort()];
            const csvRows = tableData.map(row => {
                return headers.map(h => {
                    const v = row[h];
                    if (v === undefined || v === null) return '';
                    return `"${String(v).replace(/"/g, '""')}"`;
                }).join(',');
            });
            const csvContent = [headers.join(','), ...csvRows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `metric-data-${expandedQueryId || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.success('Table data downloaded as CSV');
            return;
        }

        const timestamps = tableColumns.slice(1);
        const csvHeaders = ['metric', ...timestamps.map(t => new Date(t).toISOString().slice(0, 19).replace('T', ' '))].join(',');
        const csvRows = tableData.map(row => {
            const metric = row.metric !== undefined ? String(row.metric) : '';
            const values = timestamps.map(t => {
                const v = row[t];
                if (v === undefined || v === null) return '';
                return `"${String(v).replace(/"/g, '""')}"`;
            });
            return [metric, ...values].join(',');
        });

        const csvContent = [csvHeaders, ...csvRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `metric-data-${expandedQueryId || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success('Table data downloaded as CSV');
    };

    const handleScheduleClick = (queryId) => {
        setSelectedQueryId(queryId);
        setShowScheduleModal(true);
    };

    const handleScheduleSubmit = () => {
        if (!scheduleForm.time || !scheduleForm.start_date || !scheduleForm.mode) {
            toast.error('Please fill all required fields');
            return;
        }

        const activeQueryId = selectedQueryId || expandedQueryId;
        
        setActionLoading(prev => ({ ...prev, [activeQueryId]: 'schedule' }));
        const payload = {
            time: scheduleForm.time,
            start_date: scheduleForm.start_date,
            mode: scheduleForm.mode,
            ...(scheduleForm.scheduler_mode === 'other' && {
                scheduler_meta_meta: {
                    api: scheduleForm.api,
                    headers: scheduleForm.headers,
                    payload: scheduleForm.payload,
                    request_type: scheduleForm.request_type,
                    name: scheduleForm.name,
                    queue: scheduleForm.queue
                }
            })
        };

        axiosInstance.post(`wkl/monitoring/metric-queries/${activeQueryId}/schedule/`, payload)
            .then(() => {
                toast.success('Schedule created successfully!');
                setShowScheduleModal(false);
                setScheduleForm({ time: "", start_date: "", mode: "", scheduler_mode: "other", time_setting_meta: { week_days: [], months: [], days: [] }, api: "", headers: "", payload: "", request_type: "POST", name: "", queue: "" });
                fetchSchedules(activeQueryId);
            })
            .catch(err => toast.error(err.response?.data?.message || 'Failed to create schedule'))
            .finally(() => setActionLoading(prev => ({ ...prev, [activeQueryId]: null })));
    };

    const handleToggleSchedule = (schedulerPk, currentActive) => {
        setActionLoading(prev => ({ ...prev, [expandedQueryId]: 'toggle' }));
        axiosInstance.post(`wkl/monitoring/metric-queries/${expandedQueryId}/schedule/update/`, {
            scheduler_pk: schedulerPk,
            active: !currentActive
        })
            .then(() => {
                toast.success(`Schedule ${!currentActive ? 'activated' : 'paused'}!`);
                fetchSchedules(expandedQueryId);
            })
            .catch(err => toast.error(err.response?.data?.message || 'Failed to update schedule'))
            .finally(() => setActionLoading(prev => ({ ...prev, [expandedQueryId]: null })));
    };

    const handleDeleteSchedule = (schedulerPk) => {
        setActionLoading(prev => ({ ...prev, [expandedQueryId]: 'delete' }));
        axiosInstance.post(`wkl/monitoring/metric-queries/${expandedQueryId}/schedule/delete/`, {
        scheduler_pk: schedulerPk
        })
            .then(() => {
                toast.success('Schedule deleted successfully!');
                fetchSchedules(expandedQueryId);
            })
            .catch(err => toast.error(err.response?.data?.message || 'Failed to delete schedule'))
            .finally(() => setActionLoading(prev => ({ ...prev, [expandedQueryId]: null })));
    };

    const handleSelectQuery = (queryId) => {
        const query = queries.data?.find(q => q.id === queryId);
        if (query) {
            setSqlQuery(query.sql || '');
            setLabel(query.label || '');
            setDescription(query.description || '');
            setSelectedDatabase(query.database || '');
        }
    };

    const handleEditClick = (queryId) => {
        const query = queries.data?.find(q => q.id === queryId);
        if (query) {
            setEditModeQueryId(queryId);
            setIsEditMode(true);
            setSqlQuery(query.sql || '');
            setLabel(query.label || '');
            setDescription(query.description || '');
            setSelectedDatabase(query.database || '');
            setValidationResult(null);
        }
    };

    const handleUpdateQuery = () => {
        if (!label.trim()) {
            toast.error('Label is required');
            return;
        }
        setIsSavingEdit(true);
        const controller = new AbortController();
        axiosInstance.post(`wkl/monitoring/metric-queries/${editModeQueryId}/`, {
            label,
            description,
            database: selectedDatabase
        }, { signal: controller.signal })
            .then(() => {
                toast.success('Query updated successfully!');
                setIsEditMode(false);
                setEditModeQueryId(null);
                return axiosInstance.get('wkl/monitoring/metric-queries/', { params: { page, page_size: pageSize } });
            })
            .then(res => setQueries(res.data))
            .catch(err => toast.error(err.response?.data?.message || 'Failed to update query'))
            .finally(() => setIsSavingEdit(false));
        return () => controller.abort();
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditModeQueryId(null);
        setSqlQuery('');
        setLabel('');
        setDescription('');
        setSelectedDatabase(databases[0] || '');
        setValidationResult(null);
    };

    const handleSaveEdit = () => {
        if (!editForm.label.trim()) {
            toast.error('Label is required');
            return;
        }
        setIsSavingEdit(true);
        const controller = new AbortController();
        axiosInstance.post(`wkl/monitoring/metric-queries/${editQueryId}/`, editForm, { signal: controller.signal })
            .then(() => {
                toast.success('Query updated successfully!');
                setShowEditModal(false);
                return axiosInstance.get('wkl/monitoring/metric-queries/', { params: { page, page_size: pageSize } });
            })
            .then(res => setQueries(res.data))
            .catch(err => toast.error(err.response?.data?.message || 'Failed to update query'))
            .finally(() => {
                setIsSavingEdit(false);
            });
        return () => controller.abort();
    };

    const handleBackToList = () => {
        setExpandedQueryId(null);
        setChartData(null);
        setOverallStats(null);
        setAnomalies([]);
        setChartError(null);
        setHighlightedMetrics([]);
        setAvailableColumns([]);
        setQuerySchedules([]);
        setTableData(null);
        setTableColumns([]);
        setCollectedOn(null);
        setExecutionLogs([]);
        setSelectedLogId(null);
        setIsEditMode(false);
        setEditModeQueryId(null);
    };

    const ExpandedDetailView = () => (
        <div className="bg-slate-100 p-4 rounded-lg mb-4">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <span className="text-lg font-semibold">Query Details (ID: {expandedQueryId})</span>
                    {queries.data?.find(q => q.id === expandedQueryId) && (
                        <span className="ml-2 text-sm text-gray-600">
                            — {queries.data.find(q => q.id === expandedQueryId).label}
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => handleTrigger(expandedQueryId)}
                        disabled={actionLoading[expandedQueryId]}
                        className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
                    >
                        {actionLoading[expandedQueryId] === 'trigger' ? 'Running...' : 'Run Query'}
                    </button>
                    <button
                        onClick={handleBackToList}
                        className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                    >
                        ← Back to List
                    </button>
                </div>
            </div>

            <div className="bg-white p-3 rounded-lg mb-4">
                <div className="text-sm font-semibold mb-2">SQL Query</div>
                <pre className="font-mono text-xs bg-gray-50 p-2 rounded border overflow-x-auto">
                    {queries.data?.find(q => q.id === expandedQueryId)?.sql || 'N/A'}
                </pre>
            </div>

            <div className="bg-white p-3 rounded-lg mb-4">
                <div className="text-sm font-semibold mb-2">Execution Logs</div>
                {loadingLogs ? (
                    <div className="text-center py-4">Loading logs...</div>
                ) : executionLogs.length > 0 ? (
                    <div className="overflow-y-auto max-h-48 border rounded-lg">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-100 sticky top-0 z-10">
                                <tr>
                                    <th className="px-2 py-1 text-left border-b">ID</th>
                                    <th className="px-2 py-1 text-left border-b">Executed At</th>
                                    <th className="px-2 py-1 text-left border-b">Duration</th>
                                    <th className="px-2 py-1 text-left border-b">Triggered By</th>
                                    <th className="px-2 py-1 text-left border-b">Status</th>
                                    <th className="px-2 py-1 text-left border-b">Row Count</th>
                                    <th className="px-2 py-1 text-left border-b">Alert Level</th>
                                    <th className="px-2 py-1 text-left border-b">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {executionLogs.map(log => (
                                    <tr key={log.id} className={`border-b ${selectedLogId === log.id ? 'bg-blue-50' : ''}`}>
                                        <td className="px-2 py-1">{log.id}</td>
                                        <td className="px-2 py-1">
                                            {log.started_at ? (() => { try { const d = new Date(log.started_at); return isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 19).replace('T', ' '); } catch { return '—'; } })() : '—'}
                                        </td>
                                        <td className="px-2 py-1">{formatDuration(log.execution_time_ms)}</td>
                                        <td className="px-2 py-1">
                                            <span className={`px-2 py-0.5 rounded text-xs ${log.triggered_by === 'schedule' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {log.triggered_by}
                                            </span>
                                        </td>
                                        <td className="px-2 py-1">
                                            <span className={`px-2 py-0.5 rounded text-xs ${log.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {log.status}
                                            </span>
                                        </td>
                                        <td className="px-2 py-1">{log.row_count ?? '—'}</td>
                                        <td className="px-2 py-1">
                                            {(() => {
                                                const matchedAnomaly = anomalies.find(a => 
                                                    a.timestamp && log.started_at && 
                                                    a.timestamp.slice(0, 19) === log.started_at.slice(0, 19)
                                                );
                                                if (matchedAnomaly) {
                                                    return (
                                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                            matchedAnomaly.level === 'critical' ? 'bg-red-100 text-red-600' :
                                                            matchedAnomaly.level === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                                                            'bg-orange-100 text-orange-700'
                                                        }`}>
                                                            {matchedAnomaly.level}
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                                        log.level === 'critical' ? 'bg-red-100 text-red-700' :
                                                        log.level === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-green-100 text-green-700'
                                                    }`}>
                                                        {log.level || 'normal'}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-2 py-1">
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => handleLoadLogData(log.id)}
                                                    disabled={loadingTable && selectedLogId === log.id}
                                                    className={`px-2 py-0.5 text-xs rounded ${selectedLogId === log.id ? 'bg-blue-600 text-white' : 'bg-gray-700 text-white'} hover:bg-gray-600 disabled:opacity-50`}
                                                >
                                                    {loadingTable && selectedLogId === log.id ? 'Loading...' : 'Load'}
                                                </button>
                                                {log.blob_url && (
                                                    <button
                                                        onClick={() => handleDownloadLog(log.blob_url)}
                                                        className="px-2 py-0.5 text-xs bg-black text-white rounded hover:bg-gray-800"
                                                    >
                                                        Download
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-sm text-gray-500">No execution logs found</div>
                )}
            </div>

 <div className="bg-white p-3 rounded-lg mb-4">
                <div className="flex items-center gap-4 mb-3">
                    <div className="text-sm font-semibold">Data View</div>
                    <div className="flex gap-1">
                        <button
                            onClick={() => setActiveTab('chart')}
                            className={`px-3 py-1 text-xs rounded ${activeTab === 'chart' ? 'bg-black text-white' : 'bg-gray-200 text-black hover:bg-gray-300'}`}
                        >
                            Chart
                        </button>
                        <button
                            onClick={() => setActiveTab('table')}
                            className={`px-3 py-1 text-xs rounded ${activeTab === 'table' ? 'bg-black text-white' : 'bg-gray-200 text-black hover:bg-gray-300'}`}
                        >
                            Table
                        </button>
                        <button
                            onClick={() => setActiveTab('stats')}
                            className={`px-3 py-1 text-xs rounded ${activeTab === 'stats' ? 'bg-black text-white' : 'bg-gray-200 text-black hover:bg-gray-300'}`}
                        >
                            Stats
                        </button>
                    </div>
                </div>

                {activeTab === 'chart' && (
                    <>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="flex items-center gap-2">
                                <label className="text-sm whitespace-nowrap">No. of days:</label>
                                <Input type="number" value={days} onChange={e => setDays(e.target.value)} placeholder="Days" className="flex-1" />
                            </div>
                            <button
                                onClick={() => fetchChartData(expandedQueryId)}
                                disabled={loadingChart}
                                className="px-3 py-1 text-xs bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
                            >
                                {loadingChart ? 'Loading...' : 'Load Chart'}
                            </button>
                        </div>

                        {loadingChart ? (
                            <div className="text-center py-8">Loading...</div>
                        ) : chartError ? (
                            <div className="text-center py-8 text-red-600 bg-red-50 rounded">
                                {chartError}
                            </div>
                        ) : chartData?.data?.length > 0 ? (
                            <div>
                                <div className="mb-4 text-sm font-medium">
                                    Showing {chartData.data.length} data points for {Object.keys(chartData.metrics || {}).length} metric(s)
                                </div>
                                <div className="h-80 w-full border rounded-lg p-2 bg-white">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData.data} margin={{ top: 10, right: 30, left: 0, bottom: 30 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                            <XAxis 
                                                dataKey="x" 
                                                tick={{ fontSize: 12 }}
                                                stroke="#6b7280"
                                                tickFormatter={(value) => {
                                                    const date = new Date(value);
                                                    return date.toLocaleDateString();
                                                }}
                                            />
                                            <YAxis 
                                                tick={{ fontSize: 12 }}
                                                stroke="#6b7280"
                                            />
                                            <Tooltip 
                                                contentStyle={{ 
                                                    backgroundColor: '#fff', 
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '12px'
                                                }}
                                                labelStyle={{ fontWeight: 'bold' }}
                                                labelFormatter={(value) => new Date(value).toISOString().slice(0, 19).replace('T', ' ')}
                                            />
                                            {Object.keys(chartData.metrics || {}).sort().map((metricName, index) => {
                                                const isHighlighted = highlightedMetrics.length === 0 || highlightedMetrics.includes(metricName);
                                                return (
                                                    <Line
                                                        key={metricName}
                                                        type="monotone"
                                                        dataKey={metricName}
                                                        stroke={CHART_COLORS[index % CHART_COLORS.length]}
                                                        strokeWidth={2}
                                                        dot={{ fill: CHART_COLORS[index % CHART_COLORS.length], strokeWidth: 2, r: 4 }}
                                                        activeDot={{ r: 6 }}
                                                        name={metricName}
                                                        style={{ opacity: isHighlighted ? 1 : 0.2 }}
                                                    />
                                                );
                                            })}
                                            <Brush 
                                                dataKey="x" 
                                                height={30} 
                                                stroke="#000"
                                                fill="#f8f8f8"
                                                style={{ fontSize: 10 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2 text-xs overflow-y-auto max-h-12">
                                    {Object.keys(chartData.metrics || {}).sort().map((metricName, index) => {
                                        const isHighlighted = highlightedMetrics.length === 0 || highlightedMetrics.includes(metricName);
                                        return (
                                            <span
                                                key={metricName}
                                                onClick={() => {
                                                    if (highlightedMetrics.includes(metricName)) {
                                                        setHighlightedMetrics(highlightedMetrics.filter(m => m !== metricName));
                                                    } else {
                                                        setHighlightedMetrics([...highlightedMetrics, metricName]);
                                                    }
                                                }}
                                                className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-all ${isHighlighted ? 'bg-gray-100 font-semibold' : 'bg-gray-50 text-gray-400'}`}
                                            >
                                                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                                                {metricName}
                                            </span>
                                        );
                                    })}
                                </div>
                                {highlightedMetrics.length > 0 && (
                                    <div className="text-center mt-2">
                                        <button 
                                            onClick={() => setHighlightedMetrics([])}
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                        >
                                            Show All
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded">
                                Click "Load Chart" to view data
                            </div>
                        )}

                    </>
                )}

                {activeTab === 'table' && (
                    <>
                        <div className="grid grid-cols-3 gap-2 mb-3 items-center">
                            <div className="flex items-center gap-2">
                                <label className="text-sm whitespace-nowrap">No. of days:</label>
                                <Input type="number" value={tableDays} onChange={e => setTableDays(e.target.value)} placeholder="Days" className="flex-1" />
                            </div>
                            <button
                                onClick={() => fetchTableDataByDays(expandedQueryId)}
                                disabled={loadingTable}
                                className="px-3 py-1 text-xs bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
                            >
                                {loadingTable ? 'Loading...' : 'Load Table'}
                            </button>
                            <button
                                onClick={downloadTableAsCsv}
                                disabled={!tableData || tableData.length === 0}
                                className="px-3 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Download CSV
                            </button>
                        </div>
                        {loadingTable && <div className="text-center py-8">Loading...</div>}
                        {!loadingTable && tableData && tableData.length > 0 && tableColumns && tableColumns.length > 1 && (
                            <div className="max-h-96 overflow-auto border rounded-lg">
                                <table className="w-full text-xs border-collapse">
                                    <thead className="bg-black text-white sticky top-0 z-10">
                                        <tr>
                                            <th className="px-3 py-2 text-left border border-gray-700 font-semibold">Metric</th>
                                            {tableColumns.slice(1).map((col, idx) => {
                                                const anomalyForCol = anomalies.find(a => a.timestamp && col && a.timestamp.slice(0, 19) === col.slice(0, 19));
                                                return (
                                                    <th key={idx} className={`px-3 py-2 text-left border border-gray-700 font-semibold whitespace-nowrap ${anomalyForCol ? (anomalyForCol.level === 'critical' ? 'bg-red-600 text-white' : 'bg-yellow-500') : ''}`}>
                                                        <div className="flex flex-col">
                                                            <span>{col.slice(0, 19).replace('T', ' ')}</span>
                                                            {anomalyForCol && <span className="text-xs font-bold mt-1">{anomalyForCol.level}</span>}
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.map((row, rowIdx) => {
                                            const rowAnomalies = anomalies.filter(a => a.metric == row.metric || a.metric === row.metric);
                                            return (
                                                <tr key={rowIdx} className="border-b border-gray-200 hover:bg-gray-50">
                                                    <td className="px-3 py-2 font-semibold bg-gray-50 border border-gray-200">
                                                        {row.metric || '—'}
                                                        {rowAnomalies.length > 0 && <span className="ml-2 text-red-600 font-bold"><MdWarningAmber /></span>}
                                                    </td>
                                                    {tableColumns.slice(1).map((col, colIdx) => {
                                                        const anomalyForCell = rowAnomalies.find(a => a.timestamp && col && a.timestamp.slice(0, 19) === col.slice(0, 19));
                                                        return (
                                                            <td key={colIdx} className={`px-3 py-2 border border-gray-200 text-center ${anomalyForCell ? (anomalyForCell.level === 'critical' ? 'bg-red-100 font-bold' : 'bg-yellow-100 font-bold') : ''}`}>
                                                                {row[col] !== undefined ? row[col] : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {!loadingTable && (!tableData || tableData.length === 0) && (
                            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded">
                                Click "Load Table" to view data
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'stats' && (
                    <>
                        {!chartData ? (
                            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded">
                                Load Chart first to view statistics
                            </div>
                        ) : (
                            <div className="max-h-96 overflow-y-auto">
                                {overallStats && (
                                    <div className="mb-4 p-3 bg-black text-white rounded-lg sticky top-0 z-10 border border-gray-800 shadow-lg">
                                        <div className="text-xs font-bold mb-2 uppercase tracking-wide">Overall Stats</div>
                                        <div className="grid grid-cols-4 gap-2 text-xs">
                                            {Object.entries(overallStats).map(([key, value]) => (
                                                <div key={key} className="flex justify-between border-b border-gray-700 py-1 px-2">
                                                    <span className="text-gray-300 capitalize">{key.replace(/_/g, ' ')}:</span>
                                                    <span className="font-semibold text-white">{typeof value === 'number' ? value.toFixed(2) : value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {chartData?.metrics && Object.entries(chartData.metrics)
                                    .filter(([metricName]) => highlightedMetrics.length === 0 || highlightedMetrics.includes(metricName))
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([metricName, metricData]) => (
                                        <div key={metricName} className="mb-3 p-3 bg-gray-50 rounded-lg">
                                            <div className="text-xs font-semibold text-gray-700 mb-2">{metricName}</div>
                                            <div className="grid grid-cols-4 gap-2 text-xs">
                                                {metricData.stats && Object.entries(metricData.stats).map(([key, value]) => (
                                                    <div key={key} className="flex justify-between border-b py-1 px-2">
                                                        <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                                                        <span className="font-semibold">{typeof value === 'number' ? value.toFixed(2) : value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                }

                                {anomalies.length > 0 && (
                                    <div className="mt-4">
                                        <div className="text-xs font-semibold text-gray-700 mb-2">Anomalies</div>
                                        <div className="space-y-2">
                                            {anomalies.map((anomaly, i) => (
                                                <div key={i} className="border rounded-lg p-3 text-xs bg-white">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className={`px-2 py-0.5 rounded font-semibold ${
                                                            anomaly.level === 'critical' ? 'bg-red-100 text-red-700' :
                                                            anomaly.level === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                                            'bg-gray-100 text-gray-700'
                                                        }`}>{anomaly.level}</span>
                                                        <span className="font-semibold">{anomaly.metric}</span>
                                                        <span className="text-gray-400">{anomaly.timestamp ? anomaly.timestamp.slice(0, 19).replace('T', ' ') : '—'}</span>
                                                    </div>
                                                    <div className="grid grid-cols-4 gap-2 text-xs">
                                                        <div className="flex justify-between border-b py-1 px-2">
                                                            <span className="text-gray-600">Value:</span>
                                                            <span className="font-semibold">{anomaly.value}</span>
                                                        </div>
                                                        <div className="flex justify-between border-b py-1 px-2">
                                                            <span className="text-gray-600">Mean:</span>
                                                            <span className="font-semibold">{typeof anomaly.mean === 'number' ? anomaly.mean.toFixed(2) : anomaly.mean}</span>
                                                        </div>
                                                        <div className="flex justify-between border-b py-1 px-2">
                                                            <span className="text-gray-600">Std Dev:</span>
                                                            <span className="font-semibold">{typeof anomaly.stddev === 'number' ? anomaly.stddev.toFixed(2) : anomaly.stddev}</span>
                                                        </div>
                                                        <div className="flex justify-between border-b py-1 px-2">
                                                            <span className="text-gray-600">Deviation:</span>
                                                            <span className="font-semibold">{typeof anomaly.deviation === 'number' ? anomaly.deviation.toFixed(2) : anomaly.deviation}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {anomalies.length === 0 && (
                                    <div className="mt-4 text-xs text-gray-400 text-center py-4 bg-gray-50 rounded">
                                        No anomalies detected
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
            
            <div className="bg-white p-3 rounded-lg mb-4">
                <div className="text-sm font-semibold mb-2">Create Schedule (UTC TimeZone)</div>
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <label className="text-xs text-gray-500 mb-1 block">Schedule Time</label>
                        <Input type="time" value={scheduleForm.time} onChange={e => setScheduleForm(prev => ({ ...prev, time: e.target.value }))} placeholder="Time" />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 mb-1 block">Start Date</label>
                        <Input type="date" value={scheduleForm.start_date} onChange={e => setScheduleForm(prev => ({ ...prev, start_date: e.target.value }))} placeholder="Date" />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 mb-1 block">Mode</label>
                        <select value={scheduleForm.mode} onChange={e => setScheduleForm(prev => ({ ...prev, mode: e.target.value }))} className="border rounded px-2 py-1 text-sm w-full">
                            <option value="">Mode</option>
                            <option value="onetime">Onetime</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                </div>
                <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded border">
                    <span className="font-semibold">Note:</span> Schedules will run based on the selected mode. Onetime runs once at the specified date/time, Daily runs every day, Weekly runs on selected days, Monthly runs on the same date each month.
                </div>
                {scheduleForm.mode === 'weekly' && (
                    <div className="mt-2">
                        <label className="text-xs text-gray-500 mb-1 block">Select Days</label>
                        <Select className="w-full text-xs" options={weekdaysOptions} isMulti
                            value={weekdaysOptions.filter(it => scheduleForm.time_setting_meta?.week_days?.includes(it.value))}
                            onChange={e => setScheduleForm(prev => ({ ...prev, time_setting_meta: { ...prev.time_setting_meta, week_days: e.map(it => it.value) } }))} />
                    </div>
                )}
                <button onClick={handleScheduleSubmit} disabled={actionLoading[expandedQueryId] === 'schedule'}
                    className="mt-3 px-3 py-1 text-xs bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50">
                    {actionLoading[expandedQueryId] === 'schedule' ? '...' : 'Create Schedule'}
                </button>
            </div>

           

            <div className="bg-white p-3 rounded-lg">
                <div className="text-sm font-semibold mb-2">Schedules</div>
                {querySchedules.length > 0 ? (
                    <table className="w-full text-xs border rounded-lg overflow-hidden">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-2 py-1 text-left">ID</th>
                                <th className="px-2 py-1 text-left">Time</th>
                                <th className="px-2 py-1 text-left">Date</th>
                                <th className="px-2 py-1 text-left">Mode</th>
                                <th className="px-2 py-1 text-left">Status</th>
                                <th className="px-2 py-1 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {querySchedules.map(schedule => (
                                <tr key={schedule.scheduler_pk} className="border-b">
                                    <td className="px-2 py-1">{schedule.scheduler_pk}</td>
                                    <td className="px-2 py-1">{schedule.time}</td>
                                    <td className="px-2 py-1">{schedule.date}</td>
                                    <td className="px-2 py-1">{schedule.mode}</td>
                                    <td className="px-2 py-1">
                                        <span className={`px-2 py-0.5 rounded text-xs ${schedule.active ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`}>
                                            {schedule.active ? 'Active' : 'Paused'}
                                        </span>
                                    </td>
                                    <td className="px-2 py-1">
                                        <div className="flex gap-1">
                                            <button onClick={() => handleToggleSchedule(schedule.scheduler_pk, schedule.active)}
                                                disabled={actionLoading[expandedQueryId] === 'toggle'}
                                                className="px-2 py-0.5 text-xs bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50">
                                                {schedule.active ? 'Pause' : 'Activate'}
                                            </button>
                                            <button onClick={() => handleDeleteSchedule(schedule.scheduler_pk)}
                                                disabled={actionLoading[expandedQueryId] === 'delete'}
                                                className="px-2 py-0.5 text-xs bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50">
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="text-sm text-gray-500">No schedules created yet</div>
                )}
            </div>
        </div>
    );

    return (
        <div className="p-2 overflow-x-auto bg-white mt-4 rounded-xl mr-3 border">
            <div className="flex justify-between mb-5">
                <span className="text-xl font-medium">Metric Query Explorer</span>
            </div>

            {!expandedQueryId ? (
                <div className="bg-slate-100 p-4 rounded-lg mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <div className="text-sm font-semibold">SQL Query *</div>
                        {isEditMode && (
                            <span className="text-xs bg-black text-white px-2 py-0.5 rounded">Edit Mode</span>
                        )}
                    </div>
                    <textarea value={sqlQuery} onChange={e => setSqlQuery(e.target.value)}
                        className="border rounded-lg px-3 py-2 w-full h-32 focus:ring-0 focus:outline-gray-400 font-mono text-sm"
                        placeholder="SELECT * FROM public.wkl_metricquery limit 100"
                        disabled={isValidating || isSaving || isEditMode}
                        readOnly={isEditMode} />

                    <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                            <div className="text-sm font-semibold mb-2">Database *</div>
                            <select value={selectedDatabase} onChange={e => setSelectedDatabase(e.target.value)}
                                className="border rounded-lg px-3 py-2 w-full focus:ring-0 focus:outline-gray-400"
                                disabled={isValidating || isSaving || isEditMode}>
                                <option value="">Choose Database</option>
                                {databases.map(db => (<option key={db} value={db}>{db}</option>))}
                            </select>
                        </div>
                        <div>
                            <div className="text-sm font-semibold mb-2">Label *</div>
                            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g., test_metric" disabled={isValidating || isSaving} />
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="text-sm font-semibold mb-2">Description</div>
                        <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" disabled={isValidating || isSaving} />
                    </div>

                    {validationResult && !isEditMode && (
                        <div className={`mt-4 p-3 rounded-lg ${validationResult.valid ? 'bg-green-100' : 'bg-red-100'}`}>
                            {validationResult.valid ? (
                                <div className="text-green-700">
                                    <span className="font-semibold">✓ Valid Query</span>
                                    {validationResult.data?.columns && <div className="mt-1 text-sm">Columns: {validationResult.data.columns.join(', ')}</div>}
                                    {validationResult.data?.row_count !== undefined && <div className="text-sm">Row count: {validationResult.data.row_count}</div>}
                                </div>
                            ) : (
                                <div className="text-red-700">
                                    <span className="font-semibold">✗ Invalid Query</span>
                                    <div className="mt-1 text-sm">{validationResult.error}</div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex gap-3 mt-4">
                        {!isEditMode ? (
                            <>
                                <button onClick={handleValidate} disabled={isValidating || isSaving || !sqlQuery.trim()}
                                    className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                                    {isValidating ? 'Validating...' : 'Validate'}
                                </button>
                                <button onClick={handleSave} disabled={isValidating || isSaving || !sqlQuery.trim() || !label.trim()}
                                    className="px-4 py-2 bg-black text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                                    {isSaving ? 'Validating & Saving...' : 'Validate & Save'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={handleUpdateQuery} disabled={isSavingEdit || !label.trim()}
                                    className="px-4 py-2 bg-black text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                                    {isSavingEdit ? 'Updating...' : 'Update'}
                                </button>
                                <button onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-gray-200 text-black rounded-lg hover:bg-gray-300">
                                    Cancel
                                </button>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <ExpandedDetailView />
            )}

            <div className="mb-4">
                <div className="text-sm font-semibold mb-2">Saved Queries</div>
                <TableCustom
                    columns={[
                        ...TABLE_COLUMNS.map(col => ({
                            ...col,
                            Cell: col.key === 'createdOn' ? ({ row }) => formateDate(row.createdOn) : undefined
                        })),
                        {
                            key: 'actions',
                            label: 'Actions',
                            width: '20%',
                            Cell: ({ row }) => (
                                <div className="flex gap-1 items-center">
                                    <button onClick={() => handleTrigger(row.id)} disabled={actionLoading[row.id]}
                                        className="px-2 py-1 text-xs bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50">
                                        {actionLoading[row.id] === 'trigger' ? '...' : 'Trigger'}
                                    </button>
                                    <button onClick={() => handleEditClick(row.id)}
                                        className="px-2 py-1 text-xs bg-black text-white rounded hover:bg-gray-800">
                                        Edit
                                    </button>
                                    <button onClick={() => handleDelete(row.id)}
                                        className="px-2 py-1 text-xs bg-black text-white rounded hover:bg-gray-800">
                                        Delete
                                    </button>
                                    <button onClick={() => handleExpand(row.id)}
                                        className={`px-2 py-1 text-xs rounded ${expandedQueryId === row.id ? 'bg-black text-white' : 'bg-gray-200 text-black'} hover:bg-gray-400`}>
                                        {expandedQueryId === row.id ? '▲' : '▼'}
                                    </button>
                                </div>
                            )
                        }
                    ]}
                    data={queries}
                    loading={loading}
                    handleEdit={handleSelectQuery}
                    className="max-h-[40vh]"
                />
                <Paginator data={queries} loading={loading} setPage={setPage} setIntermidiatePageNumber={setIntermidiatePageNumber}
                    intermidiatePageNumber={intermidiatePageNumber} setPageSize={setPageSize} pageSize={pageSize} />
            </div>

            <ModalComp open={showScheduleModal} onClose={() => setShowScheduleModal(false)}>
                <div className="bg-white rounded-lg p-6 w-[600px] max-h-[80vh] overflow-y-auto">
                    <h2 className="text-lg font-semibold mb-4">Schedule Query (ID: {selectedQueryId})</h2>
                    
                    <div className="grid grid-cols-3 gap-3 mb-3">
                        <div><label className="text-sm font-semibold block mb-1">Time *</label><Input type="time" value={scheduleForm.time} onChange={e => setScheduleForm(prev => ({ ...prev, time: e.target.value }))} /></div>
                        <div><label className="text-sm font-semibold block mb-1">Start Date *</label><Input type="date" value={scheduleForm.start_date} onChange={e => setScheduleForm(prev => ({ ...prev, start_date: e.target.value }))} /></div>
                        <div><label className="text-sm font-semibold block mb-1">Mode *</label>
                            <select value={scheduleForm.mode} onChange={e => setScheduleForm(prev => ({ ...prev, mode: e.target.value }))} className="border rounded-lg px-3 py-2 w-full">
                                <option value="">Choose</option>
                                <option value="onetime">Onetime</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                            </select>
                        </div>
                    </div>

                    {scheduleForm.mode === 'weekly' && (
                        <div className="mb-3">
                            <label className="text-sm font-semibold block mb-1">Week Days</label>
                            <Select className="w-full" options={weekdaysOptions} isMulti value={weekdaysOptions.filter(it => scheduleForm.time_setting_meta?.week_days?.includes(it.value))}
                                onChange={e => setScheduleForm(prev => ({ ...prev, time_setting_meta: { ...prev.time_setting_meta, week_days: e.map(it => it.value) } }))} />
                        </div>
                    )}

                    {scheduleForm.mode === 'monthly' && (
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <div><label className="text-sm font-semibold block mb-1">Months</label><Select className="w-full" options={monthsOptions} isMulti value={monthsOptions.filter(it => scheduleForm.time_setting_meta?.months?.includes(it.value))}
                                onChange={e => setScheduleForm(prev => ({ ...prev, time_setting_meta: { ...prev.time_setting_meta, months: e.map(it => it.value) } }))} /></div>
                            <div><label className="text-sm font-semibold block mb-1">Days</label><Select className="w-full" options={dayOptions} isMulti value={dayOptions.filter(it => scheduleForm.time_setting_meta?.days?.includes(it.value))}
                                onChange={e => setScheduleForm(prev => ({ ...prev, time_setting_meta: { ...prev.time_setting_meta, days: e.map(it => it.value) } }))} /></div>
                        </div>
                    )}

                    <div className="mb-3">
                        <label className="text-sm font-semibold block mb-1">Scheduler Mode</label>
                        <select value={scheduleForm.scheduler_mode} onChange={e => setScheduleForm(prev => ({ ...prev, scheduler_mode: e.target.value }))} className="border rounded-lg px-3 py-2 w-full">
                            <option value="other">Other</option><option value="newsletter">Newsletter</option><option value="task">Task</option>
                        </select>
                    </div>

                    {scheduleForm.scheduler_mode === 'other' && (
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <div><label className="text-sm font-semibold block mb-1">API</label><Input value={scheduleForm.api} onChange={e => setScheduleForm(prev => ({ ...prev, api: e.target.value }))} placeholder="API endpoint" /></div>
                            <div><label className="text-sm font-semibold block mb-1">Request Type</label>
                                <select value={scheduleForm.request_type} onChange={e => setScheduleForm(prev => ({ ...prev, request_type: e.target.value }))} className="border rounded-lg px-3 py-2 w-full">
                                    <option value="POST">POST</option><option value="GET">GET</option>
                                </select>
                            </div>
                            <div><label className="text-sm font-semibold block mb-1">Headers</label><Input value={scheduleForm.headers} onChange={e => setScheduleForm(prev => ({ ...prev, headers: e.target.value }))} placeholder="JSON" /></div>
                            <div><label className="text-sm font-semibold block mb-1">Payload</label><Input value={scheduleForm.payload} onChange={e => setScheduleForm(prev => ({ ...prev, payload: e.target.value }))} placeholder="JSON" /></div>
                            <div><label className="text-sm font-semibold block mb-1">Name</label><Input value={scheduleForm.name} onChange={e => setScheduleForm(prev => ({ ...prev, name: e.target.value }))} /></div>
                            <div><label className="text-sm font-semibold block mb-1">Queue</label><Input value={scheduleForm.queue} onChange={e => setScheduleForm(prev => ({ ...prev, queue: e.target.value }))} /></div>
                        </div>
                    )}

                    <button onClick={handleScheduleSubmit} disabled={actionLoading[selectedQueryId] === 'schedule'} className="px-4 py-2 bg-black text-white rounded-lg disabled:opacity-50">
                        {actionLoading[selectedQueryId] === 'schedule' ? 'Creating...' : 'Create Schedule'}
                    </button>
                </div>
            </ModalComp>

            <ModalComp open={showEditModal} onClose={() => setShowEditModal(false)}>
                <div className="bg-white rounded-lg p-6 w-[700px] max-h-[80vh] overflow-y-auto">
                    <h2 className="text-lg font-semibold mb-4">Edit Query (ID: {editQueryId})</h2>
                    
                    <div className="space-y-3">
                        <div>
                            <label className="text-sm font-semibold block mb-1">Label *</label>
                            <Input value={editForm.label} onChange={e => setEditForm(prev => ({ ...prev, label: e.target.value }))} placeholder="Query label" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1">Database</label>
                            <select value={editForm.database} onChange={e => setEditForm(prev => ({ ...prev, database: e.target.value }))}
                                className="border rounded-lg px-3 py-2 w-full">
                                <option value="">Choose Database</option>
                                {databases.map(db => (<option key={db} value={db}>{db}</option>))}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1">SQL Query</label>
                            <textarea value={editForm.sql} onChange={e => setEditForm(prev => ({ ...prev, sql: e.target.value }))}
                                className="border rounded-lg px-3 py-2 w-full h-32 font-mono text-sm"
                                placeholder="SELECT * FROM ..." />
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1">Description</label>
                            <Input value={editForm.description} onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Optional description" />
                        </div>
                    </div>

                    <div className="flex gap-3 mt-4">
                        <button onClick={handleSaveEdit} disabled={isSavingEdit} className="px-4 py-2 bg-black text-white rounded-lg disabled:opacity-50">
                            {isSavingEdit ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button onClick={() => setShowEditModal(false)} className="px-4 py-2 bg-gray-200 text-black rounded-lg hover:bg-gray-300">
                            Cancel
                        </button>
                    </div>
                </div>
            </ModalComp>
        </div>
    );
};

export default MetricQueryExplorer;